import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Heart, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TurnstileWidget } from '@/components/auth/TurnstileWidget';
import { isTurnstileBypassedEnv, verifyTurnstileToken } from '@/lib/turnstile';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function Login() {
  const { t } = useLanguage();
  const { signIn, signInWithGoogle, signInWithApple, user, isHydrated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileBypassed, setTurnstileBypassed] = useState(false);

  const handleTurnstileVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(null), []);
  const handleTurnstileTimeout = useCallback(() => {
    console.warn('[Login] Turnstile load failed — allowing login without captcha');
    setTurnstileBypassed(true);
  }, []);

  // Only honor from=try if there's an actual draft saved in localStorage
  const fromTry = (() => {
    try {
      return searchParams.get('from') === 'try' && !!localStorage.getItem('sayitnow_try_draft');
    } catch { return false; }
  })();
  const fromLocation = location.state?.from as
    | { pathname?: string; search?: string; hash?: string }
    | undefined;
  const fromPath = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
    : '/dashboard';
  const from = fromTry ? '/create?from=try' : fromPath;

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema as any),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const skipTurnstile = isTurnstileBypassedEnv();
  

  // Auto-redirect if already authenticated (prevents stuck-on-login after successful sign-in)
  useEffect(() => {
    if (user && isHydrated) {
      console.log('[Login] User already authenticated, redirecting to:', from);
      navigate(from, { replace: true });
    }
  }, [user, isHydrated, from, navigate]);

  const onSubmit = async (values: LoginFormValues) => {
    console.log('[Login] Submit attempt', { skipTurnstile, turnstileBypassed, hasToken: !!turnstileToken });
    setIsLoading(true);
    try {
      // Turnstile is advisory-only for login: log but never block
      if (turnstileToken && !skipTurnstile) {
        try {
          const verification = await verifyTurnstileToken(turnstileToken);
          console.log('[Login] Turnstile verification result:', verification);
          if (!verification.success) {
            console.warn('[Login] Turnstile failed, proceeding with login anyway:', verification.reason);
          }
        } catch (turnstileErr) {
          console.warn('[Login] Turnstile error, proceeding:', turnstileErr);
        }
      }
      const { error } = await signIn(values.email, values.password);
      console.log('[Login] signIn result:', { error: error?.message ?? null });
      if (error) {
        toast({ title: t.common.error, description: error.message, variant: 'destructive' });
      } else {
        // Navigation handled by useEffect watching user+isHydrated (prevents mobile race)
      }
    } catch (err) {
      console.error('[Login] Unexpected submit error:', err);
      toast({
        title: t.common.error,
        description: err instanceof Error ? err.message : 'Unable to sign in right now. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const { error } = await signInWithGoogle();
    if (error) {
      toast({
        title: t.common.error,
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleAppleSignIn = async () => {
    const { error } = await signInWithApple();
    if (error) {
      toast({
        title: t.common.error,
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen flex bg-hero-gradient">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="absolute top-4 right-4">
            <LanguageSelector />
          </div>

          <Link to="/" className="mb-8 inline-block">
            <BrandLogo size="md" />
          </Link>

          <div className="glass rounded-3xl p-8 shadow-glass-lg">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {t.auth.loginTitle}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t.auth.loginSubtitle}
            </p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.auth.email}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="you@example.com"
                            className="pl-10 rounded-xl bg-background/60"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.auth.password}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            className="pl-10 pr-10 rounded-xl bg-background/60"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-secondary hover:underline"
                  >
                    {t.auth.forgotPassword}
                  </Link>
                </div>

                {!skipTurnstile && !turnstileBypassed && (
                  <TurnstileWidget
                    onVerify={handleTurnstileVerify}
                    onExpire={handleTurnstileExpire}
                    onLoadTimeout={handleTurnstileTimeout}
                  />
                )}

                <Button type="submit" className="w-full rounded-xl h-11" disabled={isLoading}>
                  {isLoading ? t.common.loading : t.auth.loginButton}
                </Button>
              </form>
            </Form>

            <div className="my-6 flex items-center">
              <div className="flex-1 border-t border-border/50" />
              <span className="px-4 text-sm text-muted-foreground">
                {t.auth.orContinueWith}
              </span>
              <div className="flex-1 border-t border-border/50" />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 gap-2 rounded-xl bg-background/40 border-border/50"
                onClick={handleGoogleSignIn}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t.auth.google}
              </Button>

              <Button
                variant="outline"
                className="flex-1 gap-2 rounded-xl bg-background/40 border-border/50"
                onClick={handleAppleSignIn}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {t.auth.apple}
              </Button>
            </div>

            <p className="mt-8 text-center text-sm text-muted-foreground">
              {t.auth.noAccount}{' '}
              <Link to="/signup" className="text-secondary hover:underline font-medium">
                {t.nav.signup}
              </Link>
            </p>
          </div>
        </motion.div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex flex-1 bg-warm-gradient items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10" />
        <div className="max-w-md text-center relative z-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <BrandLogo size="lg" className="mx-auto mb-8" />
          </motion.div>
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t.hero.tagline}
          </h2>
          <p className="text-muted-foreground text-lg">
            {t.hero.subheadline}
          </p>
        </div>
      </div>
    </div>
  );
}
